import {
  Inbox,
  MessageSquareReply,
  CalendarPlus,
  Clock,
  MessagesSquare,
  CheckCircle2,
} from "lucide-react";
import { Reveal, Chapter } from "../components/Reveal";

const TIMELINE = [
  {
    icon: Inbox,
    label: "Enquiry received",
    detail: "The enquiry lands through the practice's website form and is logged immediately.",
  },
  {
    icon: MessageSquareReply,
    label: "Instant response sent",
    detail: null,
    quote:
      "Thanks for your enquiry — here's some initial Invisalign information, and a link to book a consultation at a time that suits you.",
  },
  {
    icon: CalendarPlus,
    label: "Booking link offered",
    detail: "The patient can choose a consultation time directly, whenever suits them.",
  },
  {
    icon: Clock,
    label: "No booking made within 48 hours",
    detail: "The enquiry is flagged for follow-up rather than being left to go quiet.",
  },
  {
    icon: MessagesSquare,
    label: "Structured follow-up sent",
    detail: null,
    quote:
      "Just checking in — happy to answer any questions about Invisalign before you book.",
  },
  {
    icon: CheckCircle2,
    label: "Consultation booked",
    detail: "The patient books in, and the practice is notified with the full context.",
    success: true,
  },
];

export default function DemoSection() {
  return (
    <section
      id="demo"
      data-testid="demo-section"
      className="scroll-mt-24 border-t border-ink/10 bg-sand/50 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <Chapter number="04" label="See It Working" />
        </Reveal>
        <Reveal delay={60}>
          <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
            See how it works
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <span
            data-testid="demo-disclaimer-badge"
            className="mt-6 inline-block rounded-full bg-ink/[0.07] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/60"
          >
            Example — illustrative workflow, not a real patient or client result
          </span>
        </Reveal>

        <div className="mx-auto mt-14 max-w-2xl">
          <Reveal>
            <div className="flex justify-start">
              <div
                data-testid="demo-patient-message"
                className="max-w-md rounded-2xl rounded-bl-sm border border-ink/10 bg-white px-6 py-4 shadow-[0_1px_2px_rgba(31,28,24,0.05)]"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/45">
                  Patient enquiry
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink md:text-base">
                  &ldquo;Hi, I&apos;m interested in Invisalign — could you tell
                  me more about pricing?&rdquo;
                </p>
              </div>
            </div>
          </Reveal>

          <div className="relative mt-12">
            <div className="absolute bottom-8 left-[22px] top-2 w-px bg-teal-line" aria-hidden="true" />
            <div className="flex flex-col gap-8">
              {TIMELINE.map((item, i) => (
                <Reveal key={item.label} delay={i * 110}>
                  <div data-testid={`demo-timeline-step-${i}`} className="relative flex gap-5">
                    <div
                      className={`z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border ${
                        item.success
                          ? "border-teal bg-teal"
                          : "border-teal-line bg-white"
                      }`}
                    >
                      <item.icon
                        size={19}
                        strokeWidth={1.9}
                        className={item.success ? "text-white" : "text-teal"}
                      />
                    </div>
                    <div className="pt-1.5">
                      <h3
                        className={`text-sm font-semibold tracking-wide md:text-base ${
                          item.success ? "text-teal" : "text-ink"
                        }`}
                      >
                        {item.label}
                      </h3>
                      {item.detail && (
                        <p className="mt-1.5 max-w-md text-sm leading-relaxed text-ink/65">
                          {item.detail}
                        </p>
                      )}
                      {item.quote && (
                        <div className="mt-3 max-w-md rounded-xl rounded-tl-sm border border-teal-line bg-teal-soft px-5 py-3.5">
                          <p className="text-sm italic leading-relaxed text-ink/80">
                            &ldquo;{item.quote}&rdquo;
                          </p>
                        </div>
                      )}
                      {item.success && (
                        <span
                          data-testid="demo-booked-success-chip"
                          className="mt-3 inline-flex items-center gap-2 rounded-full bg-teal px-4 py-1.5 text-xs font-semibold tracking-wide text-white"
                        >
                          <CheckCircle2 size={14} />
                          Booked
                        </span>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={150}>
            <p
              data-testid="demo-caption"
              className="mt-14 text-center text-sm italic leading-relaxed text-ink/55"
            >
              This is a simplified, illustrative example of how the workflow
              operates — not a record of an actual enquiry or client.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
